import React, { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { useNavigate } from "react-router-dom";
import { FileText, Loader2, Lock, LogOut, Mail, Crown, ShieldCheck, Trash2, User, X } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { cancelSubscription, deleteAccount } from "@/lib/api";

export const AccountSheet = ({ open, onClose, subscription, onUpgrade, onSubscriptionChange }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [busy, setBusy] = useState(false);
  const [subBusy, setSubBusy] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);

  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    return () => {
      document.body.style.overflow = "";
    };
  }, [open]);

  useEffect(() => {
    if (!open) setConfirmDelete(false);
  }, [open]);

  useEffect(() => {
    const onKey = (e) => e.key === "Escape" && onClose();
    if (open) window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const handleLogout = async () => {
    setBusy(true);
    try {
      await logout();
      toast.success("Disconnesso");
    } finally {
      setBusy(false);
    }
  };

  const handleDelete = async () => {
    setBusy(true);
    try {
      await deleteAccount();
      await logout();
      toast.success("Account eliminato");
    } catch {
      toast.error("Eliminazione non riuscita");
      setBusy(false);
    }
  };

  const goLegal = (path) => {
    onClose();
    navigate(path);
  };

  const handleCancelSub = async () => {
    setSubBusy(true);
    try {
      const res = await cancelSubscription();
      onSubscriptionChange?.(res);
      toast("Abbonamento annullato");
    } catch {
      toast.error("Operazione non riuscita");
    } finally {
      setSubBusy(false);
    }
  };

  const initial = (user?.name || user?.email || "?").trim().charAt(0).toUpperCase();
  const isGoogle = user?.auth_provider === "google";

  const subStatus = subscription?.status;
  const subPlanLabel = subscription?.plan === "annual" ? "Annuale" : subscription?.plan === "monthly" ? "Mensile" : null;
  const subDays = subscription?.days_left ?? 0;

  return (
    <AnimatePresence>
      {open && (
        <div className="fixed inset-0 z-50" data-testid="account-sheet">
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.15 }}
            onClick={onClose}
            className="absolute inset-0 bg-black/70 backdrop-blur-[2px]"
          />
          <motion.div
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ duration: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="absolute inset-x-0 bottom-0 mx-auto flex max-w-[430px] flex-col overflow-hidden rounded-t-[12px] border border-b-0 border-white/[0.08] bg-[#030305]"
          >
            {/* header */}
            <div className="flex items-center justify-between border-b border-white/[0.06] px-4 pb-3 pt-3">
              <span className="mono flex items-center gap-2 text-sm font-medium tracking-[0.1em] text-white/90">
                <User size={15} className="text-white/70" />
                ACCOUNT <span className="text-white/25">//</span> LIQO
              </span>
              <button
                onClick={onClose}
                data-testid="account-close-button"
                aria-label="Chiudi"
                className="flex h-8 w-8 items-center justify-center rounded-[6px] border border-white/[0.08] text-white/55 hover:bg-white/[0.05]"
              >
                <X size={14} />
              </button>
            </div>

            <div className="px-4 py-5">
              {/* identità */}
              <div className="flex items-center gap-3 rounded-[12px] border border-white/[0.06] bg-[#0D0D14] p-4">
                <span className="mono flex h-12 w-12 shrink-0 items-center justify-center rounded-[10px] border border-[#00FF87]/30 bg-[#00FF87]/[0.06] text-lg font-light text-[#00FF87]">
                  {initial}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-light text-white/90" data-testid="account-name">
                    {user?.name || "Utente"}
                  </p>
                  <p
                    className="mono mt-0.5 flex items-center gap-1.5 truncate text-[12px] text-white/45"
                    data-testid="account-email"
                  >
                    <Mail size={11} className="shrink-0" />
                    <span className="truncate">{user?.email || "—"}</span>
                  </p>
                </div>
              </div>

              {/* badge */}
              <div className="mt-3 flex flex-wrap items-center gap-2">
                {user?.is_owner && (
                  <span
                    className="mono inline-flex items-center gap-1 rounded-[5px] border border-[#00FF87]/30 bg-[#00FF87]/[0.06] px-2 py-1 text-[10px] uppercase tracking-[0.1em] text-[#00FF87]"
                    data-testid="account-owner-badge"
                  >
                    <ShieldCheck size={11} /> Owner · dati reali
                  </span>
                )}
                <span
                  className="mono inline-flex items-center gap-1 rounded-[5px] border border-white/[0.1] bg-white/[0.03] px-2 py-1 text-[10px] uppercase tracking-[0.1em] text-white/55"
                  data-testid="account-provider-badge"
                >
                  {isGoogle ? "Google" : "Email"}
                </span>
              </div>

              {/* abbonamento */}
              {subscription && (
                <div
                  className="mt-4 rounded-[12px] border bg-[#0D0D14] p-4"
                  style={{ borderColor: subscription.is_premium ? "rgba(0,255,135,0.28)" : "rgba(255,255,255,0.08)" }}
                  data-testid="account-subscription"
                >
                  <div className="flex items-center justify-between">
                    <span className="mono flex items-center gap-1.5 text-[11px] uppercase tracking-[0.12em] text-white/45">
                      <Crown size={13} className="text-[#00FF87]" /> Abbonamento
                    </span>
                    <span
                      className="mono rounded-[5px] border px-2 py-0.5 text-[10px] uppercase tracking-[0.1em]"
                      style={
                        subStatus === "active"
                          ? { borderColor: "rgba(0,255,135,0.4)", color: "#00FF87", background: "rgba(0,255,135,0.06)" }
                          : subStatus === "trialing"
                          ? { borderColor: "rgba(255,255,255,0.18)", color: "rgba(255,255,255,0.7)" }
                          : { borderColor: "rgba(245,166,35,0.4)", color: "#F5A623", background: "rgba(245,166,35,0.06)" }
                      }
                      data-testid="account-sub-status"
                    >
                      {subStatus === "active" ? "Liqo Pro" : subStatus === "trialing" ? "In prova" : "Scaduto"}
                    </span>
                  </div>

                  <p className="mt-2 text-[13px] font-light text-white/85" data-testid="account-sub-detail">
                    {subStatus === "active"
                      ? `Piano ${subPlanLabel || "Pro"} · rinnovo tra ${subDays} ${subDays === 1 ? "giorno" : "giorni"}`
                      : subStatus === "trialing"
                      ? `Prova gratuita · ${subDays} ${subDays === 1 ? "giorno" : "giorni"} rimasti`
                      : "Nessun abbonamento attivo"}
                  </p>

                  {subStatus === "active" ? (
                    <button
                      onClick={handleCancelSub}
                      disabled={subBusy}
                      data-testid="account-sub-cancel"
                      className="mono mt-3 flex h-9 w-full items-center justify-center gap-1.5 rounded-[7px] border border-white/[0.12] text-[11px] uppercase tracking-[0.12em] text-white/55 hover:bg-white/[0.04] active:scale-[0.98] disabled:opacity-50"
                    >
                      {subBusy ? <Loader2 size={12} className="animate-spin" /> : null}
                      Annulla abbonamento
                    </button>
                  ) : (
                    <div className="mt-3 flex gap-2">
                      <button
                        onClick={() => {
                          onClose();
                          onUpgrade?.();
                        }}
                        data-testid="account-sub-upgrade"
                        className="mono flex h-10 flex-1 items-center justify-center gap-1.5 rounded-[7px] border text-[11px] font-medium uppercase tracking-[0.14em] active:scale-[0.98]"
                        style={{ borderColor: "rgba(0,255,135,0.5)", color: "#00FF87", background: "rgba(0,255,135,0.06)" }}
                      >
                        <Crown size={13} /> {subStatus === "trialing" ? "Abbonati" : "Sblocca Pro"}
                      </button>
                      {subStatus === "trialing" && (
                        <button
                          onClick={handleCancelSub}
                          disabled={subBusy}
                          data-testid="account-sub-end-trial"
                          className="mono flex h-10 items-center justify-center rounded-[7px] border border-white/[0.1] px-3 text-[10px] uppercase tracking-[0.1em] text-white/40 hover:bg-white/[0.04] active:scale-[0.98] disabled:opacity-50"
                          title="Solo per test in anteprima"
                        >
                          {subBusy ? <Loader2 size={12} className="animate-spin" /> : "Termina (test)"}
                        </button>
                      )}
                    </div>
                  )}
                </div>
              )}

              {/* legali */}
              <div className="mt-4 grid grid-cols-2 gap-2">
                <button
                  onClick={() => goLegal("/privacy")}
                  data-testid="account-privacy-link"
                  className="mono flex h-10 items-center justify-center gap-1.5 rounded-[8px] border border-white/[0.08] text-[11px] tracking-[0.08em] text-white/60 hover:bg-white/[0.04] active:scale-[0.98]"
                >
                  <Lock size={12} /> Privacy
                </button>
                <button
                  onClick={() => goLegal("/terms")}
                  data-testid="account-terms-link"
                  className="mono flex h-10 items-center justify-center gap-1.5 rounded-[8px] border border-white/[0.08] text-[11px] tracking-[0.08em] text-white/60 hover:bg-white/[0.04] active:scale-[0.98]"
                >
                  <FileText size={12} /> Termini
                </button>
              </div>

              {/* logout */}
              <button
                onClick={handleLogout}
                disabled={busy}
                data-testid="logout-button"
                className="mono mt-4 flex h-11 w-full items-center justify-center gap-2 rounded-[8px] border border-white/[0.12] bg-white/[0.04] text-[12px] font-medium uppercase tracking-[0.14em] text-white/85 hover:bg-white/[0.07] active:scale-[0.98] disabled:opacity-50"
              >
                {busy ? <Loader2 size={14} className="animate-spin" /> : <LogOut size={14} />}
                Disconnetti
              </button>

              {/* elimina account (conferma in 2 step) */}
              {!confirmDelete ? (
                <button
                  onClick={() => setConfirmDelete(true)}
                  data-testid="account-delete-button"
                  className="mono mt-2 flex h-9 w-full items-center justify-center gap-1.5 rounded-[8px] text-[11px] tracking-[0.06em] text-[#FF4D4D]/70 hover:text-[#FF4D4D] hover:bg-[#FF4D4D]/[0.05] active:scale-[0.98]"
                >
                  <Trash2 size={13} /> Elimina account
                </button>
              ) : (
                <div
                  className="mt-3 rounded-[10px] border border-[#FF4D4D]/30 bg-[#FF4D4D]/[0.05] p-3"
                  data-testid="account-delete-confirm"
                >
                  <p className="text-[12px] font-light text-white/80">
                    Sei sicuro? Questa azione è irreversibile.
                  </p>
                  {user?.is_owner && (
                    <p className="mono mt-1.5 text-[10px] leading-relaxed text-[#F5A623]">
                      Sei l'owner: i dati bancari restano nel sistema, ma perderai l'accesso al tuo
                      account.
                    </p>
                  )}
                  <div className="mt-3 flex gap-2">
                    <button
                      onClick={() => setConfirmDelete(false)}
                      disabled={busy}
                      data-testid="account-delete-cancel-button"
                      className="mono h-9 flex-1 rounded-[7px] border border-white/[0.12] text-[11px] uppercase tracking-[0.1em] text-white/70 hover:bg-white/[0.05] disabled:opacity-50"
                    >
                      Annulla
                    </button>
                    <button
                      onClick={handleDelete}
                      disabled={busy}
                      data-testid="account-delete-confirm-button"
                      className="mono flex h-9 flex-1 items-center justify-center gap-1.5 rounded-[7px] border border-[#FF4D4D]/50 bg-[#FF4D4D]/[0.12] text-[11px] font-medium uppercase tracking-[0.1em] text-[#FF4D4D] hover:bg-[#FF4D4D]/[0.18] disabled:opacity-50"
                    >
                      {busy ? <Loader2 size={13} className="animate-spin" /> : <Trash2 size={13} />}
                      Elimina
                    </button>
                  </div>
                </div>
              )}

              <p className="mono mt-5 text-center text-[10px] tracking-[0.1em] text-white/20">
                LIQO · CFO AI · sessione sicura
              </p>
            </div>
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
};
